<template>
  <DropdownMenu>
    <DropdownMenuTrigger
      :disabled="locked"
      :class="['tw:disabled:cursor-default', fullwidth && 'tw:w-full']"
    >
      <Badge
        :variant="stateVariants[state]"
        :class="fullwidth && 'tw:w-full tw:justify-center'"
      >
        <Icon v-if="icon" :name="icon" />
        {{ $t(stateLabels[state]) }}
      </Badge>
    </DropdownMenuTrigger>

    <DropdownMenuContent align="center" class="tw:min-w-32">
      <DropdownMenuRadioGroup
        :model-value="state"
        @update:model-value="emit('update:modelValue', $event as FieldState)"
      >
        <DropdownMenuRadioItem
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
        >
          {{ $t(option.label) }}
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </DropdownMenuContent>
  </DropdownMenu>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Badge } from '@/components/ui/badge'
import { Icon, type IconProps } from '@/components/ui/icon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/shadcn/components/ui/dropdown-menu'
import { isLocked, stateLabels, stateOptions, stateVariants } from './states'
import { toFieldState, type FieldState, type VisibilityConfig } from '@/types/FieldConfig'

const props = defineProps<{
  modelValue?: VisibilityConfig;
  /** Choices the field itself forbids. */
  readOnly?: VisibilityConfig;
  /** Fills its column; left off, it sizes to the state it shows. */
  fullwidth?: boolean;
  /** Names the place inside the badge, where no column header does it. */
  icon?: IconProps['name'];
}>()

const emit = defineEmits<{
  (event: 'update:modelValue', state: FieldState): void;
}>()

const state = computed<FieldState>(() => toFieldState(props.modelValue))
const options = computed(() => stateOptions(props.readOnly))
const locked = computed<boolean>(() => isLocked(props.readOnly))
</script>
